import { useEffect, useState } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Box, Stack, Typography, Skeleton, TablePagination, TableSortLabel, TextField, MenuItem,
  IconButton, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmptyState from './EmptyState';

/**
 * One table implementation for the whole app, and — importantly — one that
 * survives a phone. Below `sm` each row re-renders as a stacked card, because a
 * horizontally scrolling table is not a usable answer on a tablet or handset.
 *
 * @param columns  [{ key, header, render(row), align, hideBelow, primary, secondary }]
 *                 `primary`/`secondary` mark which columns lead the mobile card.
 * @param rows     data
 * @param rowKey   (row) => key
 * @param onRowClick optional; makes rows interactive (and keyboard-activatable)
 * @param empty    props forwarded to EmptyState
 * @param pagination  when true, only one page of rows is rendered at a time,
 *                    so a long list stays fast to paint. Paging is client-side:
 *                    all `rows` are already in memory; this just limits the DOM.
 * @param pageSize        initial rows per page (default 10)
 * @param pageSizeOptions selectable page sizes (default [10, 25, 50])
 *
 * Server-side paging (opt-in): pass `serverSide` with `totalCount`, `page`,
 * `rowsPerPage`, `onPageChange(page)` and `onRowsPerPageChange(size)`. `rows`
 * is then treated as the current page only, and the parent fetches each page.
 *
 * Sorting (opt-in, parent-controlled): give a column a `sortKey` and pass
 * `sortBy`, `sortDirection` ('asc' | 'desc') and `onSortChange(key, direction)`.
 * Clicking the active column flips the direction; another column starts at asc.
 * The table doesn't sort rows itself; the parent re-sorts or refetches.
 * On phones (no header row) a "Sort by" picker is shown above the cards.
 */
const DataTable = ({
  columns,
  rows = [],
  rowKey = (r) => r.id,
  onRowClick,
  loading = false,
  loadingRows = 5,
  empty,
  dense = false,
  pagination = false,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50],
  serverSide = false,
  totalCount = 0,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortDirection = 'asc',
  onSortChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(pageSize);

  const page = serverSide ? (controlledPage ?? 0) : localPage;
  const rowsPerPage = serverSide ? (controlledRowsPerPage ?? pageSize) : localRowsPerPage;
  const count = serverSide ? totalCount : rows.length;

  // Keep the current page valid as the underlying list changes (e.g. a search
  // narrows it): otherwise page 4 of a now-1-page result shows nothing.
  // In server mode the parent owns the page and resets it itself.
  const pageCount = Math.max(1, Math.ceil(count / rowsPerPage));
  useEffect(() => {
    if (!serverSide && localPage > pageCount - 1) setLocalPage(0);
  }, [serverSide, localPage, pageCount]);

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {Array.from({ length: loadingRows }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={dense ? 36 : 52} />
          ))}
        </Stack>
      </Paper>
    );
  }

  if (rows.length === 0 && empty) {
    return <EmptyState {...empty} />;
  }

  const interactive = Boolean(onRowClick);

  const sortable = Boolean(onSortChange);
  const sortableColumns = sortable ? columns.filter((c) => c.sortKey) : [];
  const handleSort = (key) => {
    if (!sortable) return;
    const next = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(key, sortBy === key ? next : 'asc');
  };

  const headerContent = (col) => {
    if (!sortable || !col.sortKey) return col.header;
    const active = sortBy === col.sortKey;
    return (
      <TableSortLabel
        active={active}
        direction={active ? sortDirection : 'asc'}
        onClick={() => handleSort(col.sortKey)}
      >
        {col.header}
      </TableSortLabel>
    );
  };

  const ariaSort = (col) => {
    if (!sortable || !col.sortKey) return undefined;
    if (sortBy !== col.sortKey) return 'none';
    return sortDirection === 'desc' ? 'descending' : 'ascending';
  };

  // Phones have no header row to click, so offer the same choice as a picker.
  const mobileSortBar = sortableColumns.length > 0 ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        select
        size="small"
        label="Sort by"
        value={sortBy ?? ''}
        onChange={(e) => onSortChange(e.target.value, sortDirection)}
        sx={{ flex: 1 }}
      >
        {sortableColumns.map((c) => (
          <MenuItem key={c.sortKey} value={c.sortKey}>{c.header}</MenuItem>
        ))}
      </TextField>
      <Tooltip title={sortDirection === 'desc' ? 'Descending' : 'Ascending'}>
        <IconButton
          aria-label={sortDirection === 'desc' ? 'Sorted descending, switch to ascending' : 'Sorted ascending, switch to descending'}
          onClick={() => onSortChange(sortBy, sortDirection === 'asc' ? 'desc' : 'asc')}
        >
          {sortDirection === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Stack>
  ) : null;

  const activate = (row) => (e) => {
    if (!interactive) return;
    // Let buttons and links inside a row do their own thing.
    if (e.target.closest('button, a, input, [role="button"]')) return;
    onRowClick(row);
  };

  // Only the visible slice is turned into DOM when pagination is on.
  const start = page * rowsPerPage;
  const visibleRows = pagination && !serverSide ? rows.slice(start, start + rowsPerPage) : rows;

  const handleChangePage = (_e, newPage) => {
    if (serverSide) onPageChange?.(newPage);
    else setLocalPage(newPage);
  };
  const handleChangeRowsPerPage = (e) => {
    const size = parseInt(e.target.value, 10);
    if (serverSide) {
      onRowsPerPageChange?.(size);
    } else {
      setLocalRowsPerPage(size);
      setLocalPage(0);
    }
  };

  const paginationBar = pagination ? (
    <TablePagination
      component="div"
      count={count}
      page={Math.min(page, pageCount - 1)}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={pageSizeOptions}
      labelRowsPerPage="Per page"
    />
  ) : null;

  // ---- Mobile: one card per row --------------------------------------------
  if (isMobile) {
    const primary = columns.find((c) => c.primary) || columns[0];
    const secondary = columns.find((c) => c.secondary);
    const rest = columns.filter((c) => c !== primary && c !== secondary && !c.hideOnMobile);

    return (
      <Stack spacing={1.5}>
        {mobileSortBar}
        {visibleRows.map((row) => (
          <Paper
            key={rowKey(row)}
            onClick={activate(row)}
            sx={{
              p: 2,
              cursor: interactive ? 'pointer' : 'default',
              '&:active': interactive ? { bgcolor: 'grey.50' } : undefined,
            }}
          >
            <Box sx={{ mb: rest.length ? 1.5 : 0 }}>
              {primary.render ? primary.render(row) : row[primary.key]}
              {secondary && (
                <Typography variant="body2" color="text.secondary">
                  {secondary.render ? secondary.render(row) : row[secondary.key]}
                </Typography>
              )}
            </Box>
            <Stack spacing={1}>
              {rest.map((col) => (
                <Stack key={col.key} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="caption" color="text.secondary">{col.header}</Typography>
                  <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Paper>
        ))}
        {paginationBar && <Paper sx={{ mt: 0 }}>{paginationBar}</Paper>}
      </Stack>
    );
  }

  // ---- Desktop / tablet: real table ----------------------------------------
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size={dense ? 'small' : undefined}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  aria-sort={ariaSort(col)}
                  sortDirection={sortable && sortBy === col.sortKey ? sortDirection : false}
                  sx={col.hideBelow ? { display: { xs: 'none', [col.hideBelow]: 'table-cell' } } : undefined}
                >
                  {headerContent(col)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow
                key={rowKey(row)}
                hover={interactive}
                onClick={activate(row)}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={interactive ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
                } : undefined}
                sx={{ cursor: interactive ? 'pointer' : 'default' }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={col.hideBelow ? { display: { xs: 'none', [col.hideBelow]: 'table-cell' } } : undefined}
                  >
                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Nothing to show.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {paginationBar}
    </Paper>
  );
};

export default DataTable;
